// src/brokerageadmin/QuoteyDashboard/ProspectsTab.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Pie, Bar, Line, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  ArcElement,
  Legend,
} from "chart.js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css"; // Make sure this CSS is loaded (e.g. in App.js or index.js)

import "./QuoteyDashboard.css";
import ProspectTables from "./ProspectTables";
import { getSortIndicator, sortData } from "./sortingUtils"; // Assuming it's in the same folder


const fetchApi = async (urlPath, options = {}) => {
  const serviceUrl = process.env.REACT_APP_BASICSERVICE;
  const url = `${serviceUrl}${urlPath}`;
  const token = localStorage.getItem("jwt");
  if (!token) throw new Error("Authentication token not found.");
  const response = await fetch(url, {
      method: "GET", headers: { "Content-Type": "application/json", "x-auth-token": token }, ...options,
  });
  if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`API Error (${response.status}): ${response.statusText} - ${errorBody}`);
  }
  return response.json();
};


ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement, PointElement,
  Title, Tooltip, ArcElement, Legend
);

const formatCurrency = (value) => {
  if (typeof value !== 'number') return '$0.00';
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const ProspectsTab = () => {
  // Overall Stats
  const [prospectStats, setProspectStats] = useState({ total: 0, stats: {} });
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [statsError, setStatsError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedBrokerageFilter, setSelectedBrokerageFilter] = useState('all');



  // Paginated List of Prospects
  const [paginatedProspects, setPaginatedProspects] = useState({
    prospects: [],
    total: 0, // Overall total from API, not necessarily filtered count for display
    pagination: { page: 1, limit: 10, total: 0, pages: 0 },
  });
  const [isLoadingProspects, setIsLoadingProspects] = useState(true);
  const [prospectsError, setProspectsError] = useState(null);

  // Chart Data (derived from prospectStats)
  const [chartData, setChartData] = useState({
    prospectStatusChartData: { labels: [], datasets: [] },
    prospectsByBrokerageChartData: { labels: [], datasets: [] },
    prospectsCreatedChartData: { labels: [], datasets: [] },
    conversionRateChartData: { labels: [], datasets: [] },
    prospectsByBrokerChartData: { labels: [], datasets: [] }, 
    conversionRateByVerticalChartData: { labels: [], datasets: [] }, 
    soldCountChartData: { labels: [], datasets: [] },    
    soldPremiumChartData: { labels: [], datasets: [] }, 
  });

  // Filters, Pagination, Sorting
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [filters, setFilters] = useState({ status: "all", search: "", vertical: "" }); // NEW: Add vertical filter
  const [dateRange, setDateRange] = useState([null, null]); // [startDate, endDate]
  const [startDate, endDate] = dateRange;
  const [granularity, setGranularity] = useState('day'); // NEW: Granularity for created prospects chart
  const [sortConfig, setSortConfig] = useState({ key: "updatedAt", direction: "descending" });

  // Dropdown data for filters
  const [verticalsList, setVerticalsList] = useState([]); // NEW: State for verticals dropdown
  const [brokerageList, setBrokerageList] = useState([]); // NEW: State for brokerage dropdown
  const [isLoadingFilterData, setIsLoadingFilterData] = useState(true);


  // Brokerage Detail View
  const [selectedBrokerage, setSelectedBrokerage] = useState(null);
  const [brokerageDetailView, setBrokerageDetailView] = useState(false);
  const [brokerageData, setBrokerageData] = useState({ prospects: [], stats: {} });
  const [isLoadingBrokerageDetails, setIsLoadingBrokerageDetails] = useState(false);
  const [brokerageDetailError, setBrokerageDetailError] = useState(null);


  // Fetch overall prospect statistics (filtered by date and vertical)
  const fetchProspectStatsCallback = useCallback(async () => {
    setIsLoadingStats(true);
    setStatsError(null);
    try {
      const params = new URLSearchParams({ aggregateOnly: 'true', granularity });
      if (startDate) params.append('startDate', startDate.toISOString().split("T")[0]);
      if (endDate) {
        const inclusiveEndDate = new Date(endDate);
        inclusiveEndDate.setDate(inclusiveEndDate.getDate() + 1);
        params.append('endDate', inclusiveEndDate.toISOString().split("T")[0]);
      }
      if (filters.vertical) params.append('vertical', filters.vertical);
      if (selectedBrokerageFilter && selectedBrokerageFilter !== 'all') params.append('brokerage', selectedBrokerageFilter);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      
      const data = await fetchApi(`/api/prospects/getProspectsStats?${params.toString()}`);
      setProspectStats(data);
    } catch (error) {
      setStatsError(error.message);
      setProspectStats({ total: 0, stats: {} });
    } finally {
      setIsLoadingStats(false);
    }
  }, [startDate, endDate, granularity, filters.vertical, selectedBrokerageFilter, filters.status, filters.search]);

  // Fetch paginated list of prospects (filtered by all active filters)
  const fetchPaginatedProspectsCallback = useCallback(async () => {
    setIsLoadingProspects(true);
    setProspectsError(null);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        sortBy: sortConfig.key,
        sortDir: sortConfig.direction,
      });

      if (filters.status && filters.status !== "all") params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      if (filters.vertical) params.append('vertical', filters.vertical);
      if (selectedBrokerageFilter && selectedBrokerageFilter !== 'all') { // NEW
          params.append('brokerage', selectedBrokerageFilter);
      }
      if (startDate) params.append('startDate', startDate.toISOString().split("T")[0]);
      if (endDate) {
          const inclusiveEndDate = new Date(endDate);
          inclusiveEndDate.setDate(inclusiveEndDate.getDate() + 1);
          params.append('endDate', inclusiveEndDate.toISOString().split("T")[0]);
      }

      const data = await fetchApi(`/api/prospects/getProspectsData?${params.toString()}`);
      setPaginatedProspects(data);
      if (data.pagination) {
        setPagination(prev => ({ ...prev, page: data.pagination.page, limit: data.pagination.limit }));
      }
    } catch (error) {
      setProspectsError(error.message);
      setPaginatedProspects({ prospects: [], total: 0, pagination: { page: 1, limit: 10, total: 0, pages: 0 }});
    } finally {
      setIsLoadingProspects(false);
    }
  }, [pagination.page, pagination.limit, filters.status, filters.search, filters.vertical, selectedBrokerageFilter, startDate, endDate, sortConfig]); // Dependencies

  // Fetch brokerage-specific details (with date and vertical filters)
  const fetchBrokerageDetailsCallback = useCallback(async (brokerageCode) => {
    setIsLoadingBrokerageDetails(true);
    setBrokerageDetailError(null);
    setBrokerageData({ prospects: [], stats: {} }); // Clear previous data
    try {
      let queryParams = '';
      if (startDate) queryParams += `&startDate=${startDate.toISOString().split("T")[0]}`;
      if (endDate) {
        const inclusiveEndDate = new Date(endDate);
        inclusiveEndDate.setDate(inclusiveEndDate.getDate() + 1);
        queryParams += `&endDate=${inclusiveEndDate.toISOString().split("T")[0]}`;
      }
      if (filters.vertical) queryParams += `&vertical=${encodeURIComponent(filters.vertical)}`;

      const data = await fetchApi(`/api/prospects/getBrokerageProspects/${brokerageCode}?${queryParams}`);
      setBrokerageData(data);
    } catch (error) {
      setBrokerageDetailError(error.message);
    } finally {
      setIsLoadingBrokerageDetails(false);
    }
  }, [startDate, endDate, filters.vertical]); // Add date and vertical filters to deps

  // Fetch initial dropdown data (verticals)
  useEffect(() => {
    const fetchFilterDropdownData = async () => {
        setIsLoadingFilterData(true);
        try {
            // Fetch both lists in parallel for better performance
            const [verticalsData, brokeragesData] = await Promise.all([
                fetchApi('/api/policies/getVerticalsList'),
                fetchApi('/api/brokers/getBrokerageList') // You need to create this new backend endpoint
            ]);
            setVerticalsList(verticalsData.verticals || []);
            setBrokerageList(brokeragesData.brokerages || []);
        } catch (error) {
            console.error("Failed to fetch filter dropdown data:", error);
            setVerticalsList([]);
            setBrokerageList([]);
        } finally {
            setIsLoadingFilterData(false);
        }
    };
    fetchFilterDropdownData();
  }, []);


  useEffect(() => { fetchProspectStatsCallback(); }, [fetchProspectStatsCallback]);
  useEffect(() => { fetchPaginatedProspectsCallback(); }, [fetchPaginatedProspectsCallback]);
  useEffect(() => {
    if (prospectStats && prospectStats.stats) {
      prepareChartData(prospectStats.stats);
    }
  }, [prospectStats]);
  useEffect(() => {
    if (selectedBrokerage && brokerageDetailView) {
      fetchBrokerageDetailsCallback(selectedBrokerage);
    }
  }, [selectedBrokerage, brokerageDetailView, fetchBrokerageDetailsCallback]);

  const handleBrokerageFilterChange = (event) => {
    setSelectedBrokerageFilter(event.target.value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  // --- Event Handlers ---
  const handleDateChange = (update) => {
    setDateRange(update);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when date changes
  };
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (paginatedProspects.pagination?.pages || 1)) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };
  const handleLimitChange = (event) => {
    setPagination({ page: 1, limit: parseInt(event.target.value) });
  };
  const handleStatusFilterChange = (event) => {
    setFilters(prev => ({ ...prev, status: event.target.value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  const handleVerticalFilterChange = (event) => { // NEW: Handle vertical filter
    setFilters(prev => ({ ...prev, vertical: event.target.value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  const handleGranularityChange = (event) => { // NEW: Handle granularity change
    setGranularity(event.target.value);
  };
  const handleSearchChange = (event) => {
    setFilters(prev => ({ ...prev, search: event.target.value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  const handleSearchSubmit = (event) => {
    event.preventDefault();
  };
  const handleBrokerageSelect = (brokerageCode) => {
    setSelectedBrokerage(brokerageCode);
    setBrokerageDetailView(true);
  };
  const handleVerticalSelect = (verticalName) => { // NEW: Handle vertical selection from chart for detail view (if implemented)
    // For now, this just sets the filter, could open a vertical-specific modal
    setFilters(prev => ({ ...prev, vertical: verticalName }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  const handleBackToMainView = () => {
    setBrokerageDetailView(false);
    setSelectedBrokerage(null);
    setBrokerageDetailError(null);
  };
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    } else if (sortConfig.key !== key) {
       direction = 'ascending';
    }
    setSortConfig({ key, direction });
  };

  const handleDownloadCsv = async () => {
    if (isDownloading) return; // Prevent double clicks
    setIsDownloading(true);
    
    try {
      let queryParams = [];
      // Use the same filters as the main table fetch
      if (filters.status && filters.status !== "all") queryParams.push(`status=${filters.status}`);
      if (filters.vertical) queryParams.push(`vertical=${encodeURIComponent(filters.vertical)}`);
      if (startDate) queryParams.push(`startDate=${startDate.toISOString().split("T")[0]}`);
      if (endDate) {
          const inclusiveEndDate = new Date(endDate);
          inclusiveEndDate.setDate(inclusiveEndDate.getDate() + 1);
          queryParams.push(`endDate=${inclusiveEndDate.toISOString().split("T")[0]}`);
      }
      
      const queryString = queryParams.join('&');
      const serviceUrl = process.env.REACT_APP_BASICSERVICE;
      const url = `${serviceUrl}/api/prospects/download?${queryString}`;
      const token = localStorage.getItem("jwt");

      const response = await fetch(url, {
          method: 'GET',
          headers: { 'x-auth-token': token },
      });

      if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Download failed: ${response.status} ${errorText}`);
      }

      // Handle the file blob response
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = downloadUrl;
      
      // Extract filename from the 'Content-Disposition' header
      const disposition = response.headers.get('content-disposition');
      let filename = 'prospects_report.csv'; // Default filename
      if (disposition && disposition.indexOf('attachment') !== -1) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(disposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(downloadUrl);
      a.remove();

    } catch (error) {
      console.error("Error downloading CSV:", error);
      alert("Failed to download report. Please check the console for details.");
    } finally {
      setIsDownloading(false);
    }
  };

  // --- Data Preparation & Memoization ---
  // Client-side sorting is still used for the current page only if backend doesn't sort
  const sortedPaginatedProspects = useMemo(() => {
    // If backend sorting is implemented and reliable, this might be redundant.
    // Assuming backend returns sorted data now.
    return paginatedProspects.prospects || [];
  }, [paginatedProspects.prospects]);

  const prepareChartData = (stats) => {
    if (!stats) return;
    // Common colors for consistency
    const commonColors = {
      'New': '#36A2EB', 'App In Progress': '#4BC0C0', 'Quoted': '#FFCD56', 'Bound': '#9966FF',
      'Declined': '#FF6384', 'Archived': '#B0B0B0', 'Sold': '#4CAF50', 'Client Adjustment': '#FF9800',
      'In Market': '#795548', 'Close - Income': '#607D8B', 'Close - No Ins': '#E91E63', 'default': '#CCCCCC'
    };

    const isAllBrokerages = selectedBrokerageFilter === 'all';
    const soldCountData = isAllBrokerages ? (stats.soldByTime || []) : (stats.soldByBroker || []);
    const soldCountChartData = {
      labels: soldCountData.map(item => isAllBrokerages ? item.date : item.name),
      datasets: [{
        label: 'Prospects Sold',
        data: soldCountData.map(item => item.count),
        backgroundColor: '#4CAF50', // A green color for "sold"
      }]
    };

    // 2. Sold Premium Chart Data
    const soldPremiumData = isAllBrokerages ? (stats.soldByTime || []) : (stats.soldByBroker || []);
    const soldPremiumChartData = {
      labels: soldPremiumData.map(item => isAllBrokerages ? item.date : item.name),
      datasets: [{
        label: 'Premium Sold ($)',
        data: soldPremiumData.map(item => item.premium),
        backgroundColor: '#2E8B57', // SeaGreen, a richer green
      }]
    };

    const psCD = {
      labels: stats.byStatus?.map(i => i.status) || [],
      datasets: [{
        data: stats.byStatus?.map(i => i.count) || [],
        backgroundColor: stats.byStatus?.map(i => commonColors[i.status] || commonColors.default) || [],
        borderColor: stats.byStatus?.map(i => commonColors[i.status] || commonColors.default) || [],
        borderWidth: 1
      }]
    };

    const tb = stats.byBrokerage?.sort((a,b)=>b.total-a.total).slice(0,15) || [];
    const pbCD = {
      labels: tb.map(i=>i.brokerageCode||'Unknown'),
      datasets: [
        { label: 'Total Prospects', data: tb.map(i=>i.total||0), backgroundColor: '#36A2EB' },
        { label: 'Active Prospects', data: tb.map(i=>i.active||0), backgroundColor: '#4BC0C0' },
        { label: 'Bound Prospects', data: tb.map(i=>i.binded||0), backgroundColor: '#9966FF' }
      ]
    };

    const filteredCreatedData = stats.createdData?.filter(item => item.date) || [];

    // Prospects Created by Day/Month/Year
    const pcCD = {
      labels: filteredCreatedData?.map(item => {
        if (granularity === 'day') {
          // Format 'YYYY-MM-DD' to 'DD-Mon-YY' (e.g., 10-Jun-24)
          const [year, month, day] = item.date.split('-');
          return new Date(year, month - 1, day).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
        } else if (granularity === 'week') {
            const [year, week] = item.date.split('-');
            return `Week ${week} ${year}`;
        } else if (granularity === 'year') {
            return item.date;
        }
        // For 'month' (YYYY-MM)
        const [year, month] = item.date.split('-');
        return new Date(year, month - 1).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
      }) || [],
      datasets: [{
        label:'New Prospects',
        data:stats.createdData?.map(i=>i.count)||[],
        borderColor:'#36A2EB',backgroundColor:'rgba(54,162,235,0.6)', // Solid color for bars
        borderWidth:1, type: 'bar' // Ensure it's a bar chart
      }]
    };

    // Conversion Rate by Brokerage
    const cd = tb.map(i => { const t=(i.total||0); const r= t > 0 ? parseFloat(((i.binded||0)/t*100).toFixed(1)):0; return{brokerageCode:i.brokerageCode||'Unknown',rate:r};});
    const crCD = { labels: cd.map(i=>i.brokerageCode), datasets: [{label:'Conversion Rate (%)',data:cd.map(i=>i.rate),backgroundColor:'#FF9F40',borderColor:'#FF9F40',borderWidth:1}]};

    // NEW: Prospects by Broker
    const topBrokers = stats.byBroker?.sort((a,b)=>b.total-a.total).slice(0,10) || [];
    const pbkCD = {
      labels: topBrokers.map(i=>i.name || 'Unknown Broker'),
      datasets: [{
        label:'Total Prospects',
        data:topBrokers.map(i=>i.total || 0),
        backgroundColor:'#6A5ACD', // A new distinct color
        borderColor:'#6A5ACD',
        borderWidth:1
      }]
    };

    // NEW: Conversion Rate by Vertical
    const verticalsWithConversion = stats.byVertical?.map(v => {
      const total = v.total || 0;
      const binded = v.binded || 0;
      const rate = total > 0 ? parseFloat(((binded / total) * 100).toFixed(1)) : 0;
      return { ...v, conversionRate: rate };
    })?.sort((a, b) => b.conversionRate - a.conversionRate).slice(0,10) || [];
    const crvCD = {
      labels: verticalsWithConversion.map(v => v.vertical || 'Unknown'),
      datasets: [{
        label:'Conversion Rate (%)',
        data:verticalsWithConversion.map(v => v.conversionRate),
        backgroundColor:'#8FBC8F', // Another new distinct color
        borderColor:'#8FBC8F',
        borderWidth:1
      }]
    };


    setChartData({
      prospectStatusChartData:psCD,
      prospectsByBrokerageChartData:pbCD,
      prospectsCreatedChartData:pcCD,
      conversionRateChartData:crCD,
      prospectsByBrokerChartData: pbkCD, 
      conversionRateByVerticalChartData: crvCD,
      soldCountChartData,
      soldPremiumChartData,
    });
  };

  const prepareBrokerageChartData = (brokerageStats) => {
    if (!brokerageStats) return null;
    const statusColors = {'New':'#36A2EB','App In Progress':'#4BC0C0','Quoted':'#FFCD56','Bound':'#9966FF','Declined':'#FF6384','Archived':'#B0B0B0','Sold':'#4CAF50','Client Adjustment':'#FF9800','In Market':'#795548','Close - Income':'#607D8B','Close - No Ins':'#E91E63',default:'#CCCCCC'};
    const statusChart = { labels: brokerageStats.byStatus?.map(i => i.status.replace('_',' ').toUpperCase()) || [], datasets: [{ data: brokerageStats.byStatus?.map(i=>i.count)||[], backgroundColor: brokerageStats.byStatus?.map(i=>statusColors[i.status]||statusColors.default)||[], borderWidth:1 }]};
    const monthlyChart = { labels: brokerageStats.createdByMonth?.map(i=>i.month)||[], datasets: [{label:'New Prospects',data:brokerageStats.createdByMonth?.map(i=>i.count)||[],borderColor:'#36A2EB',backgroundColor:'rgba(54,162,235,0.2)',borderWidth:2,tension:0.4,fill:true}]};
    const topBrokers = brokerageStats.byBroker?.sort((a,b)=>b.count-a.count).slice(0,10)||[];
    const brokerChart = { labels: topBrokers.map(b=>b.name||'Unknown'), datasets: [{label:'Prospects by Broker',data:topBrokers.map(b=>b.count||0),backgroundColor:'#9966FF'}]};
    return { statusChart, monthlyChart, brokerChart };
  };

  // --- Render Functions ---
  const chartOptions = { responsive:true, maintainAspectRatio:false, plugins:{legend:{position:'top'},title:{display:false}}, scales:{y:{beginAtZero:true}}};
  const doughnutOptions = {...chartOptions, cutout:'65%', plugins:{...chartOptions.plugins, legend:{position:'right',labels:{boxWidth:12,padding:15}}}};

  const renderPagination = () => {
    const { page, pages, total } = paginatedProspects.pagination || {page:1, pages:1, total:0};
    if (!pages || pages <= 1) return null;
    const pageButtons = []; const maxButtons = 5;
    pageButtons.push(<button key="prev" className={`pagination-button ${page===1?'disabled':''}`} onClick={()=>handlePageChange(page-1)} disabled={page===1}>«</button>);
    let startPage = Math.max(1, page - Math.floor(maxButtons/2)); let endPage = Math.min(pages, startPage + maxButtons -1);
    if (endPage === pages) startPage = Math.max(1, pages - maxButtons + 1);
    if (startPage > 1) { pageButtons.push(<button key={1} className={`pagination-button ${page===1?'active':''}`} onClick={()=>handlePageChange(1)}>1</button>); if (startPage > 2) pageButtons.push(<span key="ell1" className="pagination-ellipsis">...</span>); }
    for (let i=startPage; i<=endPage; i++) pageButtons.push(<button key={i} className={`pagination-button ${page===i?'active':''}`} onClick={()=>handlePageChange(i)}>{i}</button>);
    if (endPage < pages) { if (endPage < pages - 1) pageButtons.push(<span key="ell2" className="pagination-ellipsis">...</span>); pageButtons.push(<button key={pages} className={`pagination-button ${page===pages?'active':''}`} onClick={()=>handlePageChange(pages)}>{pages}</button>); }
    pageButtons.push(<button key="next" className={`pagination-button ${page===pages?'disabled':''}`} onClick={()=>handlePageChange(page+1)} disabled={page===pages}>»</button>);
    return (
      <div className="pagination-container">
        <div className="pagination-controls">{pageButtons}</div>
        <div className="pagination-info">
          <span>Showing {(page-1)*pagination.limit+1} to {Math.min(page*pagination.limit, total)} of {total}</span>
          <select className="pagination-limit" value={pagination.limit} onChange={handleLimitChange}><option value="10">10/page</option><option value="25">25/page</option><option value="50">50/page</option><option value="100">100/page</option></select>
        </div>
      </div>
    );
  };

  const renderBrokerageDetail = () => {
    if (!selectedBrokerage) return null;
    const currentBD = brokerageData; const brokerageChartData = prepareBrokerageChartData(currentBD.stats); const stats = currentBD.stats || {};
    return (<div className="brokerage-detail-view">
      <div className="detail-header"><button className="back-button" onClick={handleBackToMainView}>← Back</button><h2>{selectedBrokerage}</h2></div>
      {brokerageDetailError && <div className="error-message">Error: {brokerageDetailError}</div>}
      <div className="stats-overview">
        <div className="stat-card"><h3>Total</h3><p className="stat-value">{isLoadingBrokerageDetails?'...':stats.total??0}</p></div>
        <div className="stat-card"><h3>Active</h3><p className="stat-value">{isLoadingBrokerageDetails?'...':stats.active??0}</p></div>
        <div className="stat-card"><h3>Bound</h3><p className="stat-value">{isLoadingBrokerageDetails?'...':stats.binded??0}</p></div>
        <div className="stat-card"><h3>Rate</h3><p className="stat-value">{isLoadingBrokerageDetails?'...':`${stats.conversionRate??0}%`}</p></div>
      </div>
      <div className="charts-row">
        <div className="chart-container"><h3>Status</h3><div className="chart-wrapper">{isLoadingBrokerageDetails?<div className="lc">L...</div>:(brokerageChartData?.statusChart?<Doughnut data={brokerageChartData.statusChart} options={doughnutOptions}/>:<p>No data.</p>)}</div></div>
        <div className="chart-container"><h3>Monthly</h3><div className="chart-wrapper">{isLoadingBrokerageDetails?<div className="lc">L...</div>:(brokerageChartData?.monthlyChart?<Line data={brokerageChartData.monthlyChart} options={chartOptions}/>:<p>No data.</p>)}</div></div>
      </div>
      <div className="charts-row"><div className="chart-container full-width"><h3>By Broker</h3><div className="chart-wrapper">{isLoadingBrokerageDetails?<div className="lc">L...</div>:(brokerageChartData?.brokerChart?<Bar data={brokerageChartData.brokerChart} options={chartOptions}/>:<p>No data.</p>)}</div></div></div>
      <div className="table-container"><h3>{selectedBrokerage} Prospects ({currentBD.prospects?.length||0})</h3>
        <table className="data-table"><thead><tr><th>Client</th><th>Status</th><th>Created</th><th>Updated</th><th>Actions</th></tr></thead>
        <tbody>
          {isLoadingBrokerageDetails?(<tr><td colSpan="5"className="lc"><div className="ls"/>L...</td></tr>):brokerageDetailError?(<tr><td colSpan="5"className="edc">{brokerageDetailError}</td></tr>):currentBD.prospects?.length===0?(<tr><td colSpan="5"className="edc">No prospects.</td></tr>)
          :(currentBD.prospects.map((p)=>(<tr key={p._id}>
            <td><div className="prospect-info"><div className="prospect-name">{p.answers?.firstName||''} {p.answers?.lastName||''}</div><div className="prospect-company">{p.answers?.companyName||''}</div><div className="prospect-email">{p.answers?.email||''}</div></div></td>
            <td><span className={`status-badge status-${p.status?.toLowerCase().replace(/\s+/g,'-')}`}>{p.status?.replace('_',' ')}</span></td><td>{new Date(p.createdAt).toLocaleDateString()}</td><td>{new Date(p.updatedAt).toLocaleDateString()}</td>
            <td><div className="action-buttons"><button className="ab vb">View</button>{!p.isbinded&&!p.isarchived&&(<button className="ab eb">Edit</button>)}</div></td></tr>)))}
        </tbody></table>
      </div>
    </div>);
  };

  const renderMainView = () => {
    const stats = prospectStats.stats || {};
    const currentPaginationTotal = paginatedProspects.pagination?.total || 0;

    const premiumChartOptions = {
      ...chartOptions,
      plugins: {
          ...chartOptions.plugins,
          tooltip: {
              callbacks: {
                  label: function(context) {
                      let label = context.dataset.label || '';
                      if (label) {
                          label += ': ';
                      }
                      if (context.parsed.y !== null) {
                          label += formatCurrency(context.parsed.y);
                      }
                      return label;
                  }
              }
          }
      },
      scales: {
          y: {
              ticks: {
                  callback: function(value, index, values) {
                      return formatCurrency(value);
                  }
              }
          }
      }
  };

    return (<>
      {statsError && <div className="error-message">Stats Error: {statsError}</div>}
      <div className="table-filters">
            <div className="filter-item" style={{marginRight:'10px', display:'flex', alignItems:'center'}}><label htmlFor="date-range-picker-main" style={{marginRight:'5px',fontSize:'14px',color:'#555'}}>Date:</label>
              <DatePicker selectsRange={true} startDate={startDate} endDate={endDate} onChange={handleDateChange} isClearable={true} placeholderText="Filter by creation date" dateFormat="yyyy-MM-dd" className="filter-select" wrapperClassName="date-picker-wrapper" inputId="date-range-picker-main"/>
            </div>
            <select className="filter-select" value={filters.status} onChange={handleStatusFilterChange}>
              <option value="all">All Statuses</option><option value="new">New</option><option value="App In Progress">App In Progress</option><option value="In Market">In Market</option><option value="Binded">Binded</option><option value="Declined">Declined</option><option value="Archived">Archived</option>{/* Add other statuses as needed */}
            </select>
            {/* NEW: Vertical Filter */}
            <select className="filter-select" value={filters.vertical} onChange={handleVerticalFilterChange} disabled={isLoadingFilterData}>
                <option value="">All Verticals</option>
                {verticalsList.map(v => {
                    const displayName = v.name?.en || v.name;
                    return displayName ? <option key={v.id} value={displayName}>{displayName}</option> : null;
                })}
            </select>
            <select className="filter-select" value={selectedBrokerageFilter} onChange={handleBrokerageFilterChange} disabled={isLoadingFilterData}>
                <option value="all">All Brokerages</option>
                {brokerageList.map(brokerage => (
                  <option key={brokerage} value={brokerage}>{brokerage}</option>
                ))}
            </select>
            <form onSubmit={handleSearchSubmit} className="search-form"><input type="search" className="search-input" placeholder="Search name, company, email..." value={filters.search} onChange={handleSearchChange}/></form>
          </div>
      <div className="stats-overview">
        <div className="stat-card"><h3>Total Prospects</h3><p className="stat-value">{isLoadingStats?'...':(stats.total??0)}</p></div>
        <div className="stat-card"><h3>Active Prospects</h3><p className="stat-value">{isLoadingStats?'...':(stats.active??0)}</p></div>
        <div className="stat-card"><h3>Bound Prospects(All prospects that is binded)</h3><p className="stat-value">{isLoadingStats?'...':(stats.binded??0)}</p></div>
        <div className="stat-card"><h3>Archived Prospects</h3><p className="stat-value">{isLoadingStats?'...':(stats.archived??0)}</p></div>
        <div className="stat-card">
          <h3>Prospects Sold(bind sold value is greater than 1)</h3>
          <p className="stat-value">{isLoadingStats ? '...' : (stats.totalSold ?? 0)}</p>
        </div>
        <div className="stat-card">
          <h3>Premium Sold</h3>
          <p className="stat-value">{isLoadingStats ? '...' : formatCurrency(stats.totalPremium ?? 0)}</p>
      </div>
      </div>
      <div className="charts-row">
        <div className="chart-container"><h3>Prospect Status Distribution</h3><div className="chart-wrapper">{isLoadingStats?<div className="loading-cell">L...</div>:<Doughnut data={chartData.prospectStatusChartData||{}} options={doughnutOptions}/>}</div></div>
        <div className="chart-container">
          <div className="chart-header-dark">
            <h3>PROSPECTS CREATED</h3>
            <select value={granularity} onChange={handleGranularityChange} className="filter-select">
              <option value="day">Daily</option>
              <option value="month">Monthly</option>
              <option value="year">Yearly</option>
            </select>
          </div>
          <div className="chart-wrapper">
            {isLoadingStats ? <div className="loading-cell">L...</div> :
              chartData.prospectsCreatedChartData?.labels?.length > 0 ?
                <Bar
                  data={chartData.prospectsCreatedChartData || {}}
                  options={{
                    ...chartOptions,
                    scales: {
                      x: {
                        ticks: {
                          autoSkip: false,
                          maxRotation: 45,
                          minRotation: 30,
                          font: { size: 10 }
                        }
                      },
                      y: {
                        beginAtZero: true
                      }
                    }
                  }}
                /> : <p className="empty-data-cell">No data available.</p>}
          </div>
        </div>
      </div>
      <div className="charts-row">
        <div className="chart-container">
          <h3>{`Prospects Sold by ${selectedBrokerageFilter === 'all' ? 'Month' : 'Broker'}`}</h3>
          <div className="chart-wrapper">
            {isLoadingStats ? <div className="loading-cell">L...</div> :
              chartData.soldCountChartData?.labels?.length > 0 ?
                <Bar data={chartData.soldCountChartData} options={chartOptions}/> :
                <p className="empty-data-cell">No sold prospects match the criteria.</p>}
          </div>
        </div>
        <div className="chart-container">
          <h3>{`Premium Sold by ${selectedBrokerageFilter === 'all' ? 'Month' : 'Broker'}`}</h3>
          <div className="chart-wrapper">
            {isLoadingStats ? <div className="loading-cell">L...</div> :
              chartData.soldPremiumChartData?.labels?.length > 0 ?
                <Bar data={chartData.soldPremiumChartData} options={premiumChartOptions}/> :
                <p className="empty-data-cell">No premium data available.</p>}
          </div>
        </div>
      </div>
      <div className="charts-row">
        <div className="chart-container">
          <h3>Prospects By Brokerage (Top 15)</h3>
          <div className="chart-wrapper">
            {isLoadingStats ? (
              <div className="loading-cell">L...</div>
            ) : (
              <Bar
                data={chartData.prospectsByBrokerageChartData || {}}
                options={{
                  ...chartOptions,
                  onClick: (e, els) => {
                    if (els.length > 0) {
                      const i = els[0].index;
                      const bc = chartData.prospectsByBrokerageChartData?.labels?.[i];
                      if (bc) {
                        handleBrokerageSelect(bc);
                      }
                    }
                  },
                  scales: {
                    x: {
                      ticks: {
                        autoSkip: false,
                        maxRotation: 90,
                        minRotation: 45,
                      },
                    },
                  },
                }}
              />
            )}
          </div>
          {!isLoadingStats && (
            <div className="chart-note">Click bar for details</div>
          )}
        </div>
        <div className="chart-container">
          <h3>Conversion Rate By Brokerage (Top 15)</h3>
          <div className="chart-wrapper">
            {isLoadingStats ? <div className="loading-cell">L...</div> :
              chartData.conversionRateChartData?.labels?.length > 0 ?
                <Bar data={chartData.conversionRateChartData||{}} options={chartOptions}/> :
                <p className="empty-data-cell">No data available.</p>}
          </div>
        </div>
      </div>
      {/* NEW Charts row for Prospects by Broker and Conversion Rate by Vertical */}
      <div className="charts-row">
        <div className="chart-container">
          <h3>Top Prospects by Broker (Top 10)</h3>
          <div className="chart-wrapper">
            {isLoadingStats ? <div className="loading-cell">L...</div> :
              chartData.prospectsByBrokerChartData?.labels?.length > 0 ?
                <Bar data={chartData.prospectsByBrokerChartData || {}} options={chartOptions}/> :
                <p className="empty-data-cell">No data available.</p>}
          </div>
        </div>
        <div className="chart-container">
          <h3>Conversion Rate by Vertical (Top 10)</h3>
          <div className="chart-wrapper">
            {isLoadingStats ? <div className="loading-cell">L...</div> :
              chartData.conversionRateByVerticalChartData?.labels?.length > 0 ?
                <Bar
                  data={chartData.conversionRateByVerticalChartData || {}}
                  options={{
                    ...chartOptions,
                    onClick: (e, els) => {
                      if (els.length > 0) {
                        const i = els[0].index;
                        const verticalName = chartData.conversionRateByVerticalChartData?.labels?.[i];
                        if (verticalName) {
                            handleVerticalSelect(verticalName); // Clickable to filter table by vertical
                        }
                      }
                    },
                    plugins: {
                      ...chartOptions.plugins,
                      tooltip: {
                        callbacks: {
                          afterLabel: function(context) {
                            return 'Click to filter table'; // Hint for user
                          }
                        }
                      }
                    }
                  }}
                /> :
                <p className="empty-data-cell">No data available.</p>}
          </div>
          {!isLoadingStats && chartData.conversionRateByVerticalChartData?.labels?.length > 0 && (
            <div className="chart-note">Click bar to filter table</div>
          )}
        </div>
      </div>


      <ProspectTables prospectStats={prospectStats} isLoadingStats={isLoadingStats}/>
      <div className="table-container">
        <div className="table-header">
          <h3>Prospects ({currentPaginationTotal})</h3>
         
        </div>
        {prospectsError && <div className="error-message" style={{padding:'10px 0'}}>Error loading prospects: {prospectsError}</div>}
        <table className="data-table">
          <thead><tr>
            <th onClick={()=>requestSort('answers.firstName')} className={sortConfig.key==='answers.firstName'?'sorted':''}>Client Info {getSortIndicator('answers.firstName',sortConfig)}</th>
            <th onClick={()=>requestSort('status')} className={sortConfig.key==='status'?'sorted':''}>Status {getSortIndicator('status',sortConfig)}</th>
            <th onClick={()=>requestSort('vertical')} className={sortConfig.key==='vertical'?'sorted':''}>Vertical {getSortIndicator('vertical',sortConfig)}</th> {/* Use 'vertical' derived field */}
            <th onClick={()=>requestSort('brokerName')} className={sortConfig.key==='brokerName'?'sorted':''}>Broker {getSortIndicator('brokerName',sortConfig)}</th> {/* NEW: Broker Name column */}
            <th onClick={()=>requestSort('brokeragecode')} className={sortConfig.key==='brokeragecode'?'sorted':''}>Brokerage {getSortIndicator('brokeragecode',sortConfig)}</th>
            <th onClick={()=>requestSort('createdAt')} className={sortConfig.key==='createdAt'?'sorted':''}>Created {getSortIndicator('createdAt',sortConfig)}</th>
            <th onClick={()=>requestSort('updatedAt')} className={sortConfig.key==='updatedAt'?'sorted':''}>Last Updated {getSortIndicator('updatedAt',sortConfig)}</th>
            <th>Actions</th>
          </tr></thead>
          <tbody>
            {isLoadingProspects ? (<tr><td colSpan="8" className="loading-cell"><div className="loading-spinner"/>Loading prospects...</td></tr>)
            : prospectsError ? (<tr><td colSpan="8" className="empty-data-cell">Could not load prospects.</td></tr>)
            : sortedPaginatedProspects.length === 0 ? (<tr><td colSpan="8" className="empty-data-cell">No prospects found matching your criteria.</td></tr>)
            : (sortedPaginatedProspects.map((p)=>(
                <tr key={p._id}>
                  <td>
                    <div className="prospect-info">
                      <div className="prospect-name">{p.answers?.firstName||''} {p.answers?.lastName||''}</div>
                      {p.answers?.companyName && <div className="prospect-company">{p.answers.companyName}</div>}
                      {p.answers?.email && <div className="prospect-email">{p.answers.email}</div>}
                    </div>
                  </td>
                  <td><span className={`status-badge status-${p.status?.toLowerCase().replace(/\s+/g,'-')}`}>{p.status?.replace('_',' ')}</span></td>
                  <td>{p.vertical || 'N/A'}</td> {/* Display derived vertical name */}
                  <td>{p.brokerName || 'N/A'}</td> {/* Display derived broker name */}
                  <td><button className="brokerage-link" onClick={()=>handleBrokerageSelect(p.brokeragecode)}>{p.brokeragecode}</button></td>
                  <td>{new Date(p.createdAt).toLocaleDateString()}</td><td>{new Date(p.updatedAt).toLocaleDateString()}</td>
                  <td><div className="action-buttons"><button className="action-button view-button">View</button>{!p.isbinded&&!p.isarchived&&(<button className="action-button edit-button">Edit</button>)}</div></td>
                </tr>)))}
          </tbody>
        </table>
        <button 
            onClick={handleDownloadCsv} 
            className="action-button download-button" // Re-use existing styling or create a new one
            disabled={isDownloading}
            style={{ marginLeft: '1rem' }}
          >
            {isDownloading ? 'Downloading...' : 'Download CSV'}
          </button>
      </div>
      {renderPagination()}
    </>);
  };

  return (<div className="tab-content">{brokerageDetailView ? renderBrokerageDetail() : renderMainView()}</div>);
};
export default ProspectsTab;